import React from "react";
import { ChatbotConfigContext, type ChatbotData, type Screen2Data, type CTAData, type BottomViewData, type InitializeResponse } from "./chatbot-config-context";
import { loadWebFont, type FontKey } from "../../libs/webfont";

export function ChatbotConfigProvider({ children }: { children: React.ReactNode }) {
  const [chatbotData, setChatbotData] = React.useState<ChatbotData | null>(null);
  const [screen2Data, setScreen2Data] = React.useState<Screen2Data | null>(null);
  const [screen2DataCount, setScreen2DataCount] = React.useState<number>(0);
  const [screen2HeaderText, setScreen2HeaderText] = React.useState<string>("");
  const [ctaData, setCtaData] = React.useState<CTAData | null>(null);
  const [chatDisplayCtaData, setChatDisplayCtaData] = React.useState<CTAData | null>(null);
  const [bottomViewData, setBottomViewData] = React.useState<BottomViewData | null>(null);
  const [bottomViewItemsCount, setBottomViewItemsCount] = React.useState<number>(0);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isBotBlocked, setIsBotBlocked] = React.useState(false);

  const setChatbotConfig = React.useCallback((config: Partial<InitializeResponse>) => {
    // チャットボット停止またはサービス解約済み時はブロック状態をセットして早期リターン
    if (config.is_bot_active === false || config.is_service_cancelled === true) {
      setIsBotBlocked(true);
      setIsLoaded(true);
      return;
    }
    if (config.chatbot_data) {
      setChatbotData(config.chatbot_data);

      if (config.chatbot_data.set_font) {
        const shadowHost = document.getElementById('otai');
        const shadowRoot = shadowHost?.shadowRoot;

        if (shadowRoot) {
          loadWebFont(config.chatbot_data.set_font as FontKey, shadowRoot);
        } else {
          loadWebFont(config.chatbot_data.set_font as FontKey);
        }
      }
    }
    if (config.screen2_data) {
      setScreen2Data(config.screen2_data);
    }
    if (config.screen2_data_count !== undefined) {
      setScreen2DataCount(config.screen2_data_count);
    }
    if (config.screen2_header_text !== undefined) {
      setScreen2HeaderText(config.screen2_header_text || "");
    }
    if (config.cta_data) {
      setCtaData(config.cta_data);
    }
    if (config.chat_display_cta_data) {
      setChatDisplayCtaData(config.chat_display_cta_data);
    }
    if (config.bottom_view_data) {
      setBottomViewData(config.bottom_view_data);
    }
    if (config.bottom_view_items_count !== undefined) {
      setBottomViewItemsCount(config.bottom_view_items_count);
    }
    setIsLoaded(true);
  }, []);

  const value = React.useMemo(() => ({
    chatbotData,
    screen2Data,
    screen2DataCount,
    screen2HeaderText,
    ctaData,
    chatDisplayCtaData,
    bottomViewData,
    bottomViewItemsCount,
    setChatbotConfig,
    isLoaded,
    isBotBlocked,
  }), [chatbotData, screen2Data, screen2DataCount, screen2HeaderText, ctaData, chatDisplayCtaData, bottomViewData, bottomViewItemsCount, setChatbotConfig, isLoaded, isBotBlocked]);

  return (
    <ChatbotConfigContext.Provider value={value}>
      {children}
    </ChatbotConfigContext.Provider>
  );
}
